import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";

import { ImageOacJson } from "../types/demo";
import * as matrix from "../utils/drawing/matrix";
import * as rendering from "../utils/drawing/rendering";
import type { Transform } from "../utils/drawing/types";
import { imageViewStore } from "../utils/mobx/ImageViewStore";

const CONSTANTS = {
  EXTREME_RATIO_THRESHOLD: 10,
  CANVAS_HEIGHT_OFFSET: 150,
  INITIAL_Y_OFFSET: 60,
} as const;

const isImageExtremeRatio = (width: number, height: number) => {
  const EXTREME_RATIO_THRESHOLD = 10; // 극단적인 비율의 임계값
  const aspectRatio = width / height;
  return (
    aspectRatio > EXTREME_RATIO_THRESHOLD ||
    aspectRatio < 1 / EXTREME_RATIO_THRESHOLD
  );
};

export const useImageRendering = (
  file: ImageOacJson,
  imageScale: number,
  reset: boolean,
  imageScaleChangeHandler: (scale: number) => void,
  setCssTransform: Dispatch<SetStateAction<string>>,
) => {
  const [transform, setTransform] = useState<Transform>({
    scale: 1,
    rotation: 0,
    tx: 0,
    ty: 0,
    matrix: matrix.identity(),
  });
  const [canvasSize, setCanvasSize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  const [offset, setMouseMoveOffset] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 },
  );

  // 기본 transform 계산 로직 통합
  const calculateBaseTransform = useCallback(() => {
    if (!file) return null;

    const t0 = rendering.getImageTransform(
      canvasSize.width,
      canvasSize.height - CONSTANTS.CANVAS_HEIGHT_OFFSET,
      imageSize.width,
      imageSize.height,
    );

    return rendering.panTo({
      x: 0,
      y: CONSTANTS.INITIAL_Y_OFFSET,
      xform: t0,
    });
  }, [canvasSize, imageSize, file]);

  // transform 업데이트 로직 통합
  const updateTransform = useCallback(
    (newTransform: Transform) => {
      setTransform(newTransform);
      setCssTransform(rendering.getCssTransform(newTransform));
      imageScaleChangeHandler(newTransform.scale);
    },
    [imageScaleChangeHandler, setCssTransform],
  );

  useEffect(() => {
    if (!canvasSize.width || !canvasSize.height) return;
    const baseTransform = calculateBaseTransform();
    if (!baseTransform || baseTransform === transform) return;
    updateTransform(baseTransform);
  }, [canvasSize, imageSize, file]);

  useEffect(() => {
    const baseTransform = calculateBaseTransform();
    if (!baseTransform) return; // !reset

    const newXform = rendering.rotateBy({
      degree: baseTransform.rotation,
      xform: baseTransform,
      anchorx: canvasSize.width / 2,
      anchory: canvasSize.height / 2,
    });
    updateTransform(newXform);
  }, [reset]);

  useEffect(() => {
    if (!file) return;
    const t = rendering.zoomTo({
      newScale: imageScale,
      anchorx: canvasSize.width / 2,
      anchory: canvasSize.height / 2,
      xform: transform,
    });
    updateTransform(t);
  }, [imageScale]);

  // 마우스로 이미지를 이동시키는 이벤트 헨들러.
  useEffect(() => {
    if (!file) return;
    const t = rendering.panTo({
      x: offset.x,
      y: offset.y,
      xform: transform,
    });
    setTransform(t);
    setCssTransform(rendering.getCssTransform(t));
  }, [offset]);

  const rotateBy = useCallback(
    (degree: number) => {
      const anchorx = canvasSize.width / 2;
      const anchory = canvasSize.height / 2;

      // 기존 transform에서 맵핑한 픽셀에서 출발해서 추가 회전을 한다.
      // 따라서 anchorx, anchory 좌표는 함수 호출 시점에 화면에 보이는 좌표를 중점으로
      // 회전을 시킨다.
      const newXform = rendering.rotateBy({
        degree,
        xform: transform,
        anchorx,
        anchory,
      });
      setTransform(newXform);
      setCssTransform(rendering.getCssTransform(newXform));
    },
    [setCssTransform, transform],
  );

  const moveAndFocus = useCallback(
    (x: number, y: number, boxSize: number[], minRatio: number): number => {
      const { maxRatio } = imageViewStore;
      const canvasW = canvasSize.width;
      const canvasH = canvasSize.height;
      const imgW = imageSize.width;
      const imgH = imageSize.height;
      const boxW = boxSize[0];
      const boxH = boxSize[1];
      const r = 1;
      if (boxW === 0 || boxH === 0) return r;
      const deg2 = transform.rotation;
      // 적절한 scale 계산하기.
      const scaleW = canvasW / imgW;
      const scaleH = canvasH / imgH;
      // 박스의 크기가 화면의 N비율만큼 차지하게 한다.
      let scale = Math.min(
        (canvasW / boxW) * minRatio,
        (canvasH / boxH) * minRatio,
      );

      // 이미지의 가로세로 비율이 극단적이지 않을 경우에만 확대 비율을 조정한다.
      const adjustScale = !isImageExtremeRatio(imgW, imgH);

      if (adjustScale) {
        // 확대 비율은 maxRatio를 넘지 않는다.
        scale = Math.min(scale, Math.min(scaleH, scaleW, 1) * maxRatio);
        // 화면비율이 maxRatio를 넘기더라도 기본 값보다 작으면 확대하지 않는다.
        scale = Math.max(scale, Math.min(scaleH, scaleW, 1));

        // 박스의 크기가 화면으 minRation 보다 이미 큰경우 확대는 하지 않는다.
        if (scale < minRatio) {
          scale = Math.min(scaleH, scaleW, 1);
        }
      }

      const t2 = rendering.rotateBy({
        degree: -deg2,
        xform: transform,
        anchorx: canvasW / 2,
        anchory: canvasH / 2,
      });

      const t3 = rendering.focus({
        newScale: scale,
        anchorx: canvasW / 2 - scale * x,
        anchory: canvasH / 2 - scale * y,
        xform: t2,
      });

      // 기존 transform에서 맵핑한 픽셀에서 출발해서 추가 회전을 한다.
      // 따라서 anchorx, anchory 좌표는 함수 호출 시점에 화면에 보이는 좌표를 중점으로
      // 회전을 시킨다.
      const t4 = rendering.rotateBy({
        degree: deg2,
        xform: t3,
        anchorx: canvasW / 2,
        anchory: canvasH / 2,
      });
      setTransform(t4);
      setCssTransform(rendering.getCssTransform(t4));
      imageScaleChangeHandler(t4.scale);
      return r;
    },
    [
      imageScaleChangeHandler,
      imageSize.height,
      imageSize.width,
      setCssTransform,
      transform,
    ],
  );

  return {
    canvasSizeChangeHandler: setCanvasSize,
    canvasObjectMoveHandler: setMouseMoveOffset,
    imageSizeChangeHandler: setImageSize,
    rotateBy,
    moveAndFocus: moveAndFocus,
  };
};
